import { svgIconPaths } from '@/assets/svg/icons';
import colors from '@/theme/colors';
import { cutStringOverflow } from '@/utils/stringUtils';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Button,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import SvgIcon from '../SvgIcon';

interface Props {
  title: string;
  // sectionName -> option values; only the sections to display (e.g. selected domains).
  sections: Record<string, readonly string[]>;
  // Flat selected option values (option values are unique across sections).
  selectedOptions: string[];
  onOptionClick: (option: string) => void;
  // Returns a rendered section header (e.g. icon + label); falls back to plain text.
  sectionLabel?: (section: string) => ReactNode;
  optionLabel?: (option: string) => string;
  width?: string;
  popoverWidth?: string;
  // Placeholder shown when there are no sections (e.g. no domain chosen yet).
  emptyText?: string;
}

// Multi-select dropdown whose options are grouped into sections (one per parent),
// styled to mirror the creator/peer-support "dependent" domain -> subdomain dropdown.
const DropdownWithSections = ({
  title,
  sections,
  selectedOptions,
  onOptionClick,
  sectionLabel,
  optionLabel,
  width = '300px',
  popoverWidth,
  emptyText = 'No options available',
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const sectionEntries = Object.entries(sections);
  const hasSections = sectionEntries.length > 0;
  const noValueSelected = selectedOptions.length === 0;

  const selectedLabel = cutStringOverflow({
    str: selectedOptions.map((option) => optionLabel?.(option) ?? option).join(', '),
    length: 80,
    overflowSign: '..',
  });

  return (
    <Popover isOpen={isOpen && hasSections} onOpen={onOpen} onClose={onClose} placement={'bottom'}>
      <PopoverTrigger>
        <Button
          minHeight={'40px'}
          height={'54px'}
          width={width}
          bg={colors.extra.white}
          cursor={'pointer'}
          padding={0}
          isDisabled={!hasSections}
          border={`1px solid ${noValueSelected ? '#E2E8F0' : '#004179'}`}
          boxShadow={
            isOpen
              ? '0px 2px 8px 0px #00417933'
              : `0px 2px ${noValueSelected ? '4px 0px #00417911' : '8px 0px #00417933'}`
          }
          backgroundColor={isOpen ? 'E2E8F0' : '#FFF'}
          _hover={{ opacity: 0.7 }}
          overflow={'hidden'}
        >
          <Tooltip
            visibility={hasSections ? 'hidden' : 'visible'}
            label={emptyText}
            padding={'10px'}
            borderRadius={'6px'}
            color={'text.darkBlue'}
            backgroundColor={'extra.white'}
            boxShadow={'0px 2px 8px 0px #00417933'}
          >
            <HStack height={'100%'} width={'100%'} minWidth={0} padding={'18px'} gap={'8px'}>
              {/* minWidth:0 lets the label shrink so isTruncated can ellipsize it. */}
              <VStack flexGrow={1} minWidth={0} alignItems={'flex-start'} gap={'2px'}>
                {!noValueSelected && (
                  <Text
                    fontSize={'10px'}
                    lineHeight={'10px'}
                    fontWeight={800}
                    letterSpacing={'0.3px'}
                    color={'text.mediumGray'}
                    width={'100%'}
                    textAlign={'left'}
                    isTruncated
                  >
                    {title.toUpperCase()}
                  </Text>
                )}
                <Text
                  fontSize={noValueSelected ? '14px' : '15px'}
                  color={noValueSelected ? 'text.mediumGray' : 'text.darkBlue'}
                  lineHeight={'20px'}
                  fontWeight={noValueSelected ? 700 : 600}
                  width={'100%'}
                  textAlign={'left'}
                  isTruncated
                >
                  {noValueSelected ? (hasSections ? title : emptyText) : selectedLabel}
                </Text>
              </VStack>
              {isOpen ? (
                <ChevronUpIcon
                  boxSize={'5'}
                  flexShrink={0}
                  color={noValueSelected ? colors.text.mediumGray : colors.text.darkBlue}
                />
              ) : (
                <ChevronDownIcon
                  boxSize={'5'}
                  flexShrink={0}
                  color={noValueSelected ? colors.text.mediumGray : colors.text.darkBlue}
                />
              )}
            </HStack>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        bg="white"
        width={popoverWidth ?? width}
        maxHeight={'300px'}
        overflowY={'auto'}
        border={'1px solid #95B1C9'}
        padding={'20px'}
        paddingBottom={'15px'}
        borderRadius={'8px'}
        boxShadow={'0px 6px 12px 0px #0E006224'}
      >
        {sectionEntries.map(([section, options], index) => (
          <VStack key={section} align={'stretch'} gap={'8px'} marginTop={index > 0 ? '18px' : 0}>
            {sectionLabel ? (
              sectionLabel(section)
            ) : (
              <Text
                fontSize={'12px'}
                fontWeight={800}
                textTransform={'uppercase'}
                letterSpacing={'0.3px'}
                color={'primary.500'}
              >
                {section}
              </Text>
            )}
            {options.map((option) => {
              const isSelected = selectedOptions.includes(option);
              return (
                <HStack
                  key={option}
                  cursor={'pointer'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}
                  data-group
                  onClick={() => onOptionClick(option)}
                >
                  <Text
                    fontWeight={isSelected ? 700 : 600}
                    _groupHover={{ fontWeight: 700 }}
                    noOfLines={1}
                    color={'text.darkBlue'}
                  >
                    {optionLabel?.(option) ?? option}
                  </Text>
                  {isSelected && (
                    <SvgIcon iconPath={svgIconPaths.check} color={colors.primary[500]} size={'12'} />
                  )}
                </HStack>
              );
            })}
          </VStack>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default DropdownWithSections;
